import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';

import { useNormalizeAssetPath } from '../../../features/base/hooks/useBase';

import type { MotionKeys } from '../../../interfaces';

type Resource = {
  type: 'image' | 'video';
  src: string;
  cover_img: string;
  description?: string;
};

type MotionResources = {
  [key in MotionKeys]: Resource;
};

const useResources = () => {
  const normalizeAssetPath = useNormalizeAssetPath();
  const resourceId = useAppSelector((state) => state.filters.filters.motionType);
  const videoRoute = `${normalizeAssetPath('img/motion/video/')}`;
  const coverImgRoute = `${normalizeAssetPath('img/motion/')}`;
  const { pathname } = useLocation();

  const resources: MotionResources = {
    VERTICAL_MOTION_FREE_FALLING_MASS: {
      type: 'video',
      src: `${videoRoute}TV1.mp4`,
      cover_img: `${coverImgRoute}vertical/TV1_150dpi.png`,
    },
    VERTICAL_MOTION_DOWNWARDS_MOVING_MASS_WITH_DRIVING_FORCE: {
      type: 'video',
      src: `${videoRoute}TV4.mp4`,
      cover_img: `${coverImgRoute}vertical/TV4_150dpi.png`,
    },
    VERTICAL_MOTION_UPWARDS_MOVING_MASS_WITH_DRIVING_FORCE: {
      type: 'video',
      src: `${videoRoute}TV5.mp4`,
      cover_img: `${coverImgRoute}vertical/TV5_150dpi.png`,
    },
    HORIZONTAL_MOTION_MOVING_MASS_ON_ROLLERS: {
      type: 'video',
      src: `${videoRoute}TH1.mp4`,
      cover_img: `${coverImgRoute}horizontal/TH1_150dpi.png`,
    },
    HORIZONTAL_MOTION_MOVING_MASS_WITH_DRIVING_FORCE: {
      type: 'video',
      src: `${videoRoute}TH4.mp4`,
      cover_img: `${coverImgRoute}horizontal/TH5_150dpi.png`,
    },
    HORIZONTAL_MOTION_MOVING_MASS_ON_BELT: {
      type: 'video',
      src: `${videoRoute}TH5.mp4`,
      cover_img: `${coverImgRoute}horizontal/TH4_150dpi.png`,
    },
    INCLINE_MOTION_MOVING_MASS_DOWNWARDS_ON_INCLINED_PLANE: {
      type: 'video',
      src: `${videoRoute}TI1.mp4`,
      cover_img: `${coverImgRoute}incline/TI1_150dpi.png`,
    },
    INCLINE_MOTION_MOVING_MASS_DOWNWARDS_ON_INCLINED_PLANE_WITH_DRIVING_FORCE: {
      type: 'video',
      src: `${videoRoute}TI5.mp4`,
      cover_img: `${coverImgRoute}incline/TI5_150dpi.png`,
    },
    INCLINE_MOTION_MOVING_MASS_UPWARDS_ON_INCLINED_PLANE_WITH_DRIVING_FORCE: {
      type: 'video',
      src: `${videoRoute}TI7.mp4`,
      cover_img: `${coverImgRoute}incline/TI7_150dpi.png`,
    },
    ROTATIONAL_MOTION_FREELY_MOVING_LEVER_AT_VERTICAL_IMPACT: {
      type: 'video',
      src: `${videoRoute}RS2.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS2_150dpi.png`,
    },
    ROTATIONAL_MOTION_UPWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AT_VERTICAL_IMPACT: {
      type: 'video',
      src: `${videoRoute}RS3.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS3_150dpi.png`,
    },
    ROTATIONAL_MOTION_DOWNWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AT_VERTICAL_IMPACT: {
      type: 'video',
      src: `${videoRoute}RS1.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS1_150dpi.png`,
    },
    ROTATIONAL_MOTION_FREELY_MOVING_LEVER_WITH_SIDE_IMPACT: {
      type: 'video',
      src: `${videoRoute}RS6.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS6_150dpi.png`,
    },
    ROTATIONAL_MOTION_DOWNWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AND_SIDE_IMPACT: {
      type: 'video',
      src: `${videoRoute}RS5.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS5_150dpi.png`,
    },
    ROTATIONAL_MOTION_UPWARDS_MOVING_LEVER_WITH_DRIVING_TORQUE_AND_SIDE_IMPACT: {
      type: 'video',
      src: `${videoRoute}RS4.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS4_150dpi.png`,
    },
    ROTATIONAL_MOTION_ROTATING_LEVER_ON_HORIZONTAL_PLANE_WITH_DRIVING_TORQUE: {
      type: 'video',
      src: `${videoRoute}RS7.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS7_150dpi.png`,
    },
    ROTATIONAL_MOTION_ROTATING_DISK_ON_VERTICAL_PLANE: {
      type: 'video',
      src: `${videoRoute}RS9.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS9_150dpi.png`,
    },
    ROTATIONAL_MOTION_SWINGING_LINKAGE_WITH_DRIVING_FORCE: {
      type: 'video',
      src: `${videoRoute}RS11.mp4`,
      cover_img: `${coverImgRoute}rotatory-movement/RS11_150dpi.png`,
    },
  };
  const [resource, setResource] = useState(resources[resourceId]);

  useEffect(() => {
    const routeMotionType = pathname.split('/')[1].replaceAll('-', '_').toLocaleUpperCase();

    setResource(resources[routeMotionType as keyof typeof resources]);
  }, []);

  return resource;
};

export default useResources;
